import { IDateService } from "@/core/ports";
import { DayjsManager } from "@/infrastructure/services/dayJs";
import { ManipulateType } from "dayjs";

export type DateUnit = ManipulateType;

export class DateAdapter implements IDateService {
  private readonly dayjs = new DayjsManager().getInstance();

  getCurrentDate(): Date {
    return this.dayjs().toDate();
  }

  format(date: string, format: string): string {
    return this.dayjs(date).format(format);
  }

  add(date: string, amount: number, unit: DateUnit): Date {
    return this.dayjs(date).add(amount, unit).toDate();
  }

  subtract(date: string, amount: number, unit: DateUnit): Date {
    return this.dayjs(date).subtract(amount, unit).toDate();
  }

  isBefore(date1: string, date2: string): boolean {
    return this.dayjs(date1).isBefore(date2);
  }

  isAfter(date1: string, date2: string): boolean {
    return this.dayjs(date1).isAfter(date2);
  }

  diff(date1: string, date2: string, unit: DateUnit): number {
    return this.dayjs(date1).diff(date2, unit);
  }

  convertToTimeZone(date: string, timezone: string): Date {
    return this.dayjs(date).tz(timezone).toDate();
  }

  formatInTimeZone(date: string, timezone: string, format: string): string {
    return this.dayjs(date).tz(timezone).format(format);
  }

  getTimeZoneOffset(timezone: string): number {
    return this.dayjs().tz(timezone).utcOffset();
  }

  startOfDay(date: string): Date {
    return this.dayjs(date).startOf("day").toDate();
  }

  endOfDay(date: string): Date {
    return this.dayjs(date).endOf("day").toDate();
  }

  startOfMonth(date: string): Date {
    return this.dayjs(date).startOf("month").toDate();
  }

  isWeekend(date: string): boolean {
    const day = this.dayjs(date).day();
    return day === 0 || day === 6;
  }

  isBusinessDay(date: string): boolean {
    return !this.isWeekend(date);
  }

  isValid(date: string): boolean {
    return this.dayjs(date).isValid();
  }

  isValidDate(str: string): boolean {
    if (typeof str !== "string" || str === "") return false;

    // Normalize separators to '/'
    const normalized = str.trim().replaceAll(/[-.\s]+/g, "/");
    const parts = normalized.split("/");
    if (parts.length !== 3) return false;

    const nums = parts.map(Number);
    if (nums.some(Number.isNaN)) return false;

    const [month, day, year] = nums;

    // Validate ranges
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return false;

    // Verify real date
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
  }

  parseDateFlexible(str: string): Date | null {
    if (typeof str !== "string") return null;
    str = str.trim();

    // Handle ISO strings directly
    const isoDate = new Date(str);
    if (!Number.isNaN(isoDate.getTime())) return isoDate;

    // Normalize separators to "/"
    const normalized = str.replaceAll(/[-.\s]+/g, "/");
    const parts = normalized.split("/");
    if (parts.length !== 3) return null;

    const nums = parts.map(Number);
    if (nums.some(Number.isNaN)) return null;

    const [month, day, year] = nums;

    // Validate ranges
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000 || year > 9999) return null;

    // Construct Date object
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) return null;

    return date;
  }

  isBeforeOrEqual(date1: string, date2: string): boolean {
    return this.dayjs(date1).isBefore(date2) || this.dayjs(date1).isSame(date2);
  }

  getQuarter(date: string): number {
    return this.dayjs(date).quarter();
  }

  timeUntil(date: string): string {
    const hours = this.dayjs(date).diff(this.dayjs(), "hours");
    if (hours < 24) {
      return `${hours} hours`;
    }
    const days = Math.floor(hours / 24);
    if (days === 1) {
      return "1 day";
    }
    return `${days} days`;
  }
}
